import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Address } from '@core/interfaces';
import { AddressValidationService, Validation } from '@core/services/address-validation/address-validation.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { QueryAddressGeocodeGQL } from 'app/main/locations/services/location.service';
import { Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() addressForm: FormGroup;
  @Input() isViewMode: boolean;
  @Input() editableValue = true;

  public inputValue = new FormControl([Validators.required]);
  public selectedAddress: string | null;
  public loading = false;
  public addresses = [];
  public validation: Validation;
  private readonly removeSubscriptions = new Subject<void>();

  constructor(
    private addressGeocodeGQL: QueryAddressGeocodeGQL,
    private addressValidationService: AddressValidationService,
    private fuseTranslationLoaderService: FuseTranslationLoaderService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public ngOnInit(): void {
    const address = this.addressForm.value;

    if (!address.formattedAddress) {
      this.deselectAddress();
    } else {
      this.selectedAddress = address.formattedAddress;
      this.inputValue.setValue(address);
      this.inputValue.setErrors(null);
    }

    this.inputValue.valueChanges
      .pipe(
        takeUntil(this.removeSubscriptions),
        debounceTime(1000)
      ).subscribe((value): void => {
        this.addresses = [];
        if (!this.selectedAddress && this.validation) {
          for (const key of Object.keys(this.validation)) {
            delete this.validation[key];
          }
        }

        if (value && typeof value === 'string') {
          this.searchAddressGeocode(value);
        }
      });

    this.addressForm.get('formattedAddress').valueChanges.subscribe((value) => {
      this.selectedAddress = value;
    });

    this.addressForm.valueChanges.subscribe(() => {
      this.validation = this.addressValidationService.validateAddress(this.addressForm.value);
    });
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public setSelectedAddress(): void {
    if (this.inputValue && this.inputValue.value) {
      const address = this.getAddressToSave(this.inputValue.value);
      this.selectedAddress = address.formattedAddress;

      this.addressForm.get('coords').setValue({
        latitude: address.coords.latitude,
        longitude: address.coords.longitude
      });

      this.validation = this.addressValidationService.validateAddress(address);
      if (this.validation && this.validation.errors && this.validation.errors.length) {
        this.addressForm.setErrors({
          invalid: true
        });

        return;
      }
      this.addressForm.patchValue(address);
    }
  }

  getAddressToSave(address): Address {
    return {
      postalCode: address.postalCode && address.postalCode.replace(/[^a-zA-Z0-9]/g, ''),
      city: address.city,
      state: address.state,
      streetName: address.streetName,
      number: address.number,
      neighborhood: address.neighborhood,
      formattedAddress: address.formattedAddress,
      complement: address.complement,
      coords: {
        latitude: address.coords.latitude,
        longitude: address.coords.longitude
      }
    };
  }

  public deselectAddress(): void {
    this.inputValue.setValue(this.selectedAddress);
    this.selectedAddress = null;
  }

  private searchAddressGeocode(formattedAddress: string): void {
    this.loading = true;
    const input = {
      formattedAddress,
      language: navigator.language || 'pt-BR'
    };

    this.addressGeocodeGQL
      .fetch({ input })
      .pipe(map(({ data }) => data.addressGeocode))
      .subscribe((addressGeocode) => {
        this.addresses = addressGeocode;
        this.loading = false;
      });
  }
}
